/* GrowthOS — Product record (view) page.
   Rebuilt to the shared `.gos-leadrec` / `gos-lr-*` detail shell (mirrors the Sales /
   Quote / Lead detail pages): back row, header card (product glyph + name + Active
   badge + code/owner meta, owner pill, Edit + dots), chips, a horizontal tab bar, a
   read-only Overview (info cards) and the real Accounts (ChildAccount) / Attachments /
   Activity(Timeline) / Notes child tabs. Replaces the legacy left-nav scroll-spy that
   reused ProductForm in "view mode" (ProductForm is now a plain editable form). Same
   fetches as the legacy page (fetchDetail/Notes/Attachments/AccountsAndAppendToProduct,
   module 'Product'). */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAccountsAndAppendToProduct,
  fetchAttachmentsAndAppendToModule,
  fetchDetailAndAppendToModule,
  fetchNotesAndAppendToModule,
} from '../../redux/actions';
import { getProductById } from '../../redux/selector';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import Loader from '../../shared/component/Loader';
import Notes from '../sharedChild/note/notes';
import Attachments from '../sharedChild/attachment/attachments';
import Timeline from '../sharedChild/timeline';
import ChildAccount from '../sharedChild/childAccount';
import { Ic } from "../../shared/component/GosIcon";


const GOS_AVA_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const gosInitials = (name) =>
  String(name || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase() || '?';
const gosAvaColor = (name) =>
  GOS_AVA_COLORS[String(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % GOS_AVA_COLORS.length];

const CURRENCY = process.env.REACT_APP_CURRENCY || '$';
const money = (n) => `${CURRENCY} ${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const val = (v) => (v === 0 || v ? v : null);
const fmtDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
};
const fmtDT = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const activeBadge = (on) => (
  <span className={'gos-badge gos-b-' + (on ? 'green' : 'gray')}>
    <span className="gos-pip" />
    {on ? 'Active' : 'Inactive'}
  </span>
);

const InfoCard = ({ title, icon, rows }) => (
  <div className="gos-lr-card">
    <div className="gos-lr-card-head">
      <div className="gos-lr-card-title">
        <Ic name={icon} size={16} /> {title}
      </div>
    </div>
    <div className="gos-lr-card-rows">
      {rows.map(([label, value], i) => (
        <div className="gos-lr-drow" key={i}>
          <div className="gos-lr-dlabel">{label}</div>
          <div className={'gos-lr-dval' + (val(value) ? '' : ' muted')}>{val(value) ?? '—'}</div>
        </div>
      ))}
    </div>
  </div>
);

const TextCard = ({ title, icon, text }) => (
  <div className="gos-lr-card">
    <div className="gos-lr-card-head">
      <div className="gos-lr-card-title">
        <Ic name={icon} size={16} /> {title}
      </div>
    </div>
    <div className={'gos-lr-textbody' + (text ? '' : ' muted')}>{text || 'No content provided.'}</div>
  </div>
);

export const ViewProduct = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const stateData = useSelector((state) => state);
  const storeLoading = isLoadingByKey(stateData, 'productList');
  const [loading, setLoading] = useState(storeLoading);

  const searchParams = new URLSearchParams(location.search);
  const productID = searchParams.get('id');

  const [productLocalData, setProductLocalData] = useState({ ...(location.state || {}), type: 'view', from: 'view' });
  const productStoreData = useSelector((state) => getProductById(state, location.state?.id || productID));

  useEffect(() => {
    setProductLocalData((prev) => ({ ...prev, ...productStoreData }));
  }, [productStoreData]);

  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  useEffect(() => {
    const productId = location.state?.id || productID;
    if (productId) {
      dispatch(fetchDetailAndAppendToModule({ moduleName: 'Product', id: productId, localDetail: location.state || null }));
      dispatch(fetchNotesAndAppendToModule({ moduleName: 'Product', id: productId }));
      dispatch(fetchAttachmentsAndAppendToModule({ moduleName: 'Product', id: productId }));
      dispatch(fetchAccountsAndAppendToProduct({ moduleName: 'Product', id: productId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, productLocalData?.id]);

  const cd = productLocalData || {};
  const owner = cd.productOwner;
  const active = !!cd.productActive;

  const handleEditProduct = () => {
    setMenuOpen(false);
    navigate('/products/productList/editProduct', { state: { ...cd, type: 'view', from: 'view' } });
  };

  const chips = [
    cd.unitPrice ? { icon: 'tag', label: 'Unit Price ' + money(cd.unitPrice) } : null,
    { icon: active ? 'box' : 'box', label: active ? 'Active' : 'Inactive', tone: active ? 'green' : 'gray' },
    owner && { icon: 'user', label: 'Owner ' + owner },
    cd.createdDateTime && { icon: 'clock', label: 'Created ' + fmtDT(cd.createdDateTime) },
  ].filter(Boolean);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'grid' },
    { id: 'accounts', label: 'Accounts', icon: 'building' },
    { id: 'attachments', label: 'Attachments', icon: 'paperclip', count: cd.attachments?.length },
    { id: 'activity', label: 'Activity', icon: 'clock' },
    { id: 'notes', label: 'Notes', icon: 'note', count: cd.notesList?.length },
  ];

  const overview = (
    <>
      <div className="gos-lr-grid2">
        <InfoCard
          title="Product Information"
          icon="tag"
          rows={[
            ['Product Owner', val(owner)],
            ['Product Code', val(cd.productCode)],
            ['Vendor Name', val(cd.vendorName)],
            ['Manufacturer', val(cd.manufacturer)],
            ['Product Category', val(cd.productCategory)],
            ['Active', activeBadge(active)],
          ]}
        />
        <InfoCard
          title="Price Information"
          icon="tag"
          rows={[
            ['Unit Price', cd.unitPrice ? money(cd.unitPrice) : null],
            ['Commission Rate', val(cd.commissionRate) != null ? cd.commissionRate + '%' : null],
            ['Tax', val(cd.tax)],
          ]}
        />
        <InfoCard
          title="Stock Information"
          icon="box"
          rows={[
            ['Usage Unit', val(cd.usageUnit)],
            ['Handler', val(cd.handler)],
            ['Qty Ordered', val(cd.quantityOrdered)],
            ['Qty In Stock', val(cd.quantityInStock)],
            ['Qty In Demand', val(cd.quantityInDemand)],
            ['Reorder Level', val(cd.reorderLevel)],
          ]}
        />
        <InfoCard
          title="Availability"
          icon="calendar"
          rows={[
            ['Sales Start Date', val(fmtDate(cd.salesStartDate))],
            ['Sales End Date', val(fmtDate(cd.salesEndDate))],
            ['Support Start Date', val(fmtDate(cd.supportStartDate))],
            ['Support End Date', val(fmtDate(cd.supportEndDate))],
            ['Created Date', val(fmtDT(cd.createdDateTime))],
          ]}
        />
      </div>
      <div className="gos-lr-grid2" style={{ marginTop: 16 }}>
        <TextCard title="Description" icon="note" text={cd.description} />
      </div>
    </>
  );

  let tabBody = overview;
  if (activeTab === 'accounts') tabBody = <ChildAccount localData={cd} module={'Product'} />;
  else if (activeTab === 'attachments') tabBody = <Attachments localData={cd} module={'Product'} />;
  else if (activeTab === 'activity') tabBody = <Timeline localData={cd} module={'Product'} />;
  else if (activeTab === 'notes') tabBody = <Notes leadLocalData={cd} module={'Product'} />;

  return (
    <>
      {loading && <Loader />}
      <div className="ViewLeadPageOuter gos-leadrec">
        {/* back row */}
        <div className="gos-lr-backrow">
          <button type="button" className="gos-lr-ghost-btn" onClick={() => navigate('/products/productList')}>
            <Ic name="chevL" size={15} /> Products
          </button>
          {cd.code ? (
            <div className="gos-lr-leadid">
              Product <span>{cd.code}</span>
            </div>
          ) : null}
        </div>

        {/* header card */}
        <div className="gos-lr-card gos-lr-header">
          <div className="gos-lr-header-top">
            <div className="gos-lr-header-l">
              <span className="gos-lr-ava" style={{ background: 'var(--gos-accent-soft)', color: 'var(--gos-accent)' }}>
                <Ic name="tag" size={22} />
              </span>
              <div className="gos-lr-header-meta">
                <div className="gos-lr-name" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  {cd.name || 'Product'}
                  {activeBadge(active)}
                </div>
                <div className="gos-lr-subline">
                  {cd.productCode ? (
                    <>
                      <Ic name="grid" size={14} />
                      {cd.productCode}
                    </>
                  ) : (
                    <>
                      <Ic name="grid" size={14} />
                      {cd.code || '—'}
                    </>
                  )}
                  {cd.productCategory ? (
                    <>
                      <span className="gos-lr-dot">·</span>
                      {cd.productCategory}
                    </>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="gos-lr-actions">
              {owner ? (
                <span className="gos-qr-owner">
                  <span className="gos-lr-ava" style={{ width: 24, height: 24, fontSize: 10.5, background: gosAvaColor(owner) }}>
                    {gosInitials(owner)}
                  </span>
                  {owner}
                </span>
              ) : null}
              <button type="button" className="gos-lr-contact-btn" onClick={handleEditProduct} title="Edit product">
                <Ic name="edit" size={16} /> Edit
              </button>
              <button
                type="button"
                className="gos-lr-iconbtn gos-lr-iconbtn-bordered"
                onClick={() => setMenuOpen((o) => !o)}
                aria-label="More actions"
              >
                <Ic name="dots" size={18} />
              </button>
              {menuOpen ? (
                <>
                  <div className="gos-lr-menu-backdrop" onClick={() => setMenuOpen(false)} />
                  <div className="gos-lr-menu">
                    <button type="button" className="gos-lr-menu-item" onClick={handleEditProduct}>
                      <Ic name="edit" size={15} /> Edit Product
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          </div>
          {chips.length ? (
            <div className="gos-lr-chips">
              {chips.map((c, i) => (
                <span className={'gos-lr-chip' + (c.tone ? ' gos-lr-chip-' + c.tone : '')} key={i}>
                  <Ic name={c.icon} size={14} /> {c.label}
                </span>
              ))}
            </div>
          ) : null}
        </div>

        {/* tabs */}
        <div className="gos-lr-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              className={'gos-lr-tab' + (activeTab === t.id ? ' active' : '')}
              onClick={() => setActiveTab(t.id)}
            >
              <Ic name={t.icon} size={15} /> {t.label}
              {t.count != null ? <span className="gos-lr-count">{t.count}</span> : null}
            </button>
          ))}
        </div>

        {/* body */}
        {tabBody}
      </div>
    </>
  );
};

export default ViewProduct;
